<template>
  <div class="article-video-container">
    <div v-if="posterUrl" class="article-video-image">
      <img :src="posterUrl" />
    </div>
    <div class="article-video">
      <video
        ref="videoDom"
        playsinline
        :attr="[autoLoad ? 'data-auto-load preload' : 'preload=none']"
        :poster="posterUrl"
        :src="videoSrc"
        @play="onVideoPlay()"
        @pause="onVideoPause()"
        @seeking="onVideoSeek()"
        @seeked="onVideoSeeked()"
        @timeupdate="onVideoTimeUpdate()"
        @loadedmetadata="onVideoLoadedMetaData()"
        @waiting="onVideoWaiting()"
        @canplay="onVideoCanPlay()"
        @playing="onVideoPlaying()"
        @mouseenter="onVideoControl($event, true)"
        @mouseleave="onVideoControl($event, !isPlaying)"
        @touchend="onVideoTouchEnd($event)"
      ></video>
      <div v-if="showLoad" class="article-video-loading"></div>
      <div
        ref="volumeControlDom"
        class="article-video-volume controls"
        :class="{
          'hide-controls': !toShowControls,
        }"
        @mouseenter="onVideoControl($event, true)"
        @mouseleave="onVideoControl($event, !isPlaying)"
      >
        <div class="volume-status">
          <VolumeOn v-if="videoVolume >= 0.5" class="icon-volume" />
          <VolumeLow v-if="videoVolume > 0 && videoVolume < 0.5" class="icon-volume" />
          <VolumeOff v-if="videoVolume === 0" class="icon-volume" />
        </div>
        <div class="volume-slider">
          <div class="volume-slider-indicator circle small"></div>
          <div class="volume-slider-overlay"></div>
          <div class="volume-slider-base"></div>
        </div>
      </div>
      <div
        ref="fullScreenDom"
        class="article-video-full-screen controls"
        :class="{ 'hide-controls': !toShowControls }"
        @mouseenter="onVideoControl($event, true)"
        @mouseleave="onVideoControl($event, !isPlaying)"
      >
        <Max />
      </div>
      <div
        ref="playBtnDom"
        class="article-video-play-button play controls"
        :class="{ 'hide-controls': !toShowControls }"
        @mouseenter="onVideoControl($event, true)"
        @mouseleave="onVideoControl($event, !isPlaying)"
      >
        <Play v-if="!isPlaying" class="icon-play" />
        <Pause v-if="isPlaying" class="icon-pause" />
        <span v-if="!isPlaying" class="play-caption">Watch the video</span>
      </div>
      <div
        v-show="active"
        ref="progressBarFullDom"
        class="article-video-bottom-bar controls"
        :class="{ 'hide-controls': !toShowControls }"
        @mouseenter="onVideoControl($event, true)"
        @mouseleave="onVideoControl($event, !isPlaying)"
      >
        <div ref="progressBarDom" class="loaded-progress-bar"></div>
      </div>
      <div ref="unsupportedDom" class="article-video-not-supported hidden">
        <h4>Your browser does not support video playback.</h4>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue';
import { DeviceUtils } from '@/pixel/index';
// import ShakaPlayer from 'shaka-player';
import SupportUtils from '../commons/SupportUtils.js';

import VolumeOn from '@/assets/video/volume-on.svg';
import VolumeLow from '@/assets/video/volume-low.svg';
import VolumeOff from '@/assets/video/volume-off.svg';
import Max from '@/assets/video/max.svg';
import Play from '@/assets/video/play.svg';
import Pause from '@/assets/video/pause.svg';

export const EVENT_ARTICLE_VIDEO_PLAY = 'play';
export const EVENT_ARTICLE_VIDEO_PAUSE = 'pause';
export const EVENT_ARTICLE_VIDEO_INTERACTING = 'interact';
export const EVENT_ARTICLE_VIDEO_CONTROL_VISIBLE = 'EVENT_ARTICLE_VIDEO_CONTROL_VISIBLE';
export const EVENT_ARTICLE_VIDEO_CONTROL_HIDDEN = 'EVENT_ARTICLE_VIDEO_CONTROL_HIDDEN';

export const ANALYTICS_EVENT = {
  played: 'video_played',
  paused: 'video_paused',
  viewed: 'video_viewed',
  finished: 'video_finished',
  progress: 'video_progress',
  unsupported: 'video_unsupported',
};

let ShakaPlayer = null;

export default {
  name: 'Video',
  props: {
    active: Boolean,
    poster: String,
    autoLoad: Boolean,
    dash: String,
    hls: String,
    mp4: String,
    modelValue: Boolean,
    product: Object,
  },
  components: {
    VolumeOn,
    VolumeLow,
    VolumeOff,
    Max,
    Play,
    Pause,
  },
  data() {
    return {
      vSrc: '',
      videoDuration: 0,
      MAX_VOLUME_HEIGHT: 120,
      isVideoPlayerSetUp: false,
      isPlaying: false,
      played: false,
      isTouchMove: false,
      toShowControls: true,
      showControlsTimeOut: null,
      isControlVisible: true,
      shakaPlayer: null,
      hasFinishedWatching: false,
      showLoad: false,
      pageType: 'product',
      percentageArray: [],
      isCompatible: null,
      currentProgress: 0,
      progressThresholds: [0, 25, 50, 75, 95],
      savedVolume: 0.3,
      videoVolume: 0.3,
      hoverDisabled: false,
      listenerOptions: {
        capture: false,
        passive: true,
      },
      videoSrc: null,
    };
  },
  setup(props) {
    const videoDom = ref(null);
    const playBtnDom = ref(null);
    const progressBarFullDom = ref(null);
    const progressBarDom = ref(null);
    const fullScreenDom = ref(null);
    const volumeControlDom = ref(null);
    const unsupportedDom = ref(null);

    const posterUrl = ref(null);

    onMounted(() => {
      if (videoDom.value) {
        posterUrl.value = props.poster ? props.poster.replace('{width}', videoDom.value.clientWidth).replace('{q}', 60) : null;
      }
    });

    return {
      videoDom,
      playBtnDom,
      progressBarFullDom,
      progressBarDom,
      fullScreenDom,
      volumeControlDom,
      unsupportedDom,
      posterUrl,
    };
  },
  watch: {
    async active(value) {
      if (value) {
        !this.isVideoPlayerSetUp && (await this.setUpVideoPlayer());
        this.onVideoInteract();
      } else {
        this.videoDom.pause();
      }
    },
    modelValue(value) {
      if (value) {
        this.play();
      } else {
        this.pause();
      }
    },
  },
  async mounted() {
    ShakaPlayer = await import('shaka-player');
    ShakaPlayer.polyfill.installAll();
    this.$emit('update:modelValue', false);

    if (!SupportUtils.passiveListener) {
      this.listenerOptions = false;
    }

    await nextTick();

    this.initVideoControls();
    this.setUpSeekBar();
    this.setUpFullscreen();
    this.setUpVolume();

    if (this.autoLoad) {
      this.setUpVideoPlayer();
    }

    this.videoDuration = this.videoDom.duration;

    this.checkSupported();
  },
  methods: {
    /**
     * Set up src, or play ability of the video player
     * Support HLS on Safari on MacOS and iOS
     * Support DASH on everything else
     * Support MP4 if all of the above fails
     * Show "unsupported" message if it's beyond savable
     * Playing DASH on MacOS Safari has flickering effect at the beginning, suspecting it's due to DASH MPD format
     */
    async setUpVideoPlayer() {
      const { userAgent } = window.navigator;

      if (DeviceUtils.isiOS() || (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent))) {
        this.videoSrc = this.hls;
        this.isVideoPlayerSetUp = true;
      } else if (ShakaPlayer.Player.isBrowserSupported()) {
        try {
          await this.shakaPlayer.load(this.dash);
        } catch (error) {
          this.videoSrc = this.mp4;
        }
        this.isVideoPlayerSetUp = true;
      } else {
        this.videoSrc = this.mp4;
        this.isVideoPlayerSetUp = true;
      }
    },

    /**
     * Initialize all of the video controls
     */
    initVideoControls() {
      this.showNativeVideoControl(false);

      // if (DeviceUtils.isMobile()) {
      //     this.videoDom.addEventListener("touchend", this.onVideoTouchEnd);
      // } else {
      //     const eventHandler = (toShow) => {
      //         clearTimeout(this.showControlsTimeOut);
      //         this.showControls(toShow);
      //     }

      //     let videoControls = this._articleVideoContainer.querySelectorAll(`.controls`);
      //     if (videoControls && videoControls.length) {
      //         for (let i = 0; i < videoControls.length; i++) {
      //             let controlDom = videoControls[i];
      //             controlDom.addEventListener("mouseenter", () => {
      //                 eventHandler(true);
      //             });
      //             controlDom.addEventListener("mouseleave", () => {
      //                 eventHandler(!this.isPlaying);
      //             });
      //         }
      //     }

      //     this.videoDom.addEventListener("mousemove", () => {
      //         eventHandler(true);
      //         if (this.isPlaying) {
      //             this.setHideControlsTimeOut();
      //         }
      //     });

      //     this.videoDom.addEventListener("mouseleave", () => {
      //         eventHandler(!this.isPlaying);
      //     });
      // }

      this.disableHoverOnMobile(this.playBtnDom);

      const onPlayHandler = (event) => {
        event.preventDefault();
        clearTimeout(this.showControlsTimeOut);
        if (!this.isTouchMove) {
          this[this.isPlaying ? 'pause' : 'play']();
        }
        this.isTouchMove = false;
      };

      this.playBtnDom.addEventListener(DeviceUtils.isMobile() ? 'touchend' : 'click', onPlayHandler);
      this.videoDom.addEventListener(DeviceUtils.isMobile() ? 'touchend' : 'click', onPlayHandler);

      const onTouchMoveHandler = () => {
        this.isTouchMove = true;
      };
      this.playBtnDom.addEventListener('touchmove', onTouchMoveHandler, this.listenerOptions);
      this.videoDom.addEventListener('touchmove', onTouchMoveHandler, this.listenerOptions);

      this.onVideoInteract();

      if (ShakaPlayer.Player.isBrowserSupported()) {
        this.shakaPlayer = new ShakaPlayer.Player(this.videoDom);
        this.shakaPlayer.configure({
          abr: {
            defaultBandwidthEstimate: this.getBandwidthEstimate(),
          },
          streaming: {
            rebufferingGoal: 2,
            bufferingGoal: 10,
            bufferBehind: 15,
          },
        });
      }
    },

    onVideoPlay() {
      this.isPlaying = true;
      this.played = true;
      this.emitEvent(EVENT_ARTICLE_VIDEO_PLAY);
      this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
      clearTimeout(this.showControlsTimeOut);
      this.setHideControlsTimeOut();
      this.trackEvent(ANALYTICS_EVENT.played);
      this.progressBarFullDom.classList.remove('hidden');
    },

    onVideoPause() {
      clearTimeout(this.showControlsTimeOut);
      this.isPlaying = false;
      this.emitEvent(EVENT_ARTICLE_VIDEO_PAUSE);
      this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
      this.showControls(true);
      this.trackEvent(ANALYTICS_EVENT.paused);
    },

    onVideoSeek() {
      this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
    },

    onVideoSeeked() {
      this.showLoad = false;
    },

    onVideoTimeUpdate() {
      this.updateProgress(this.videoDom?.currentTime ?? 0);
      this.sendProgress();
      if (!this.hasFinishedWatching && this.percentageArray.length > 95) {
        this.hasFinishedWatching = true;
        this.trackEvent(ANALYTICS_EVENT.finished);
      }
    },

    onVideoLoadedMetaData() {
      if (this.videoDom) {
        this.videoDuration = this.videoDom.duration;
        this.setUpSeekBar();
        this.refreshPercentageArray();
      }
    },

    onVideoWaiting() {
      this.showLoad = true;
    },

    onVideoCanPlay() {
      this.showLoad = false;
    },

    onVideoPlaying() {
      this.showLoad = false;
    },

    onVideoTouchEnd(event) {
      if (!this.isVideoFullScreen) {
        event.preventDefault();
      }
      clearTimeout(this.showControlsTimeOut);
      if (!this.isControlVisible) {
        this.setHideControlsTimeOut();
      }
      if (this.isPlaying) {
        this.toggleControls();
      }
    },

    onVideoControl(event, toShow) {
      clearTimeout(this.showControlsTimeOut);
      this.showControls(toShow);
    },

    showNativeVideoControl(toShow) {
      if (this.videoDom) {
        this.videoDom.controls = toShow;
        this.videoDom.classList[toShow ? 'remove' : 'add']('disable-controls');
      }
    },

    refreshPercentageArray() {
      const interval = setInterval(() => {
        if (!this.videoDom) {
          clearInterval(interval);
        } else {
          const percentage = Math.round((this.videoDom?.currentTime ?? 0 / this.videoDuration) * 100);
          if (!this.percentageArray.includes(percentage) && percentage) {
            this.percentageArray.push(percentage);
          }
        }
      }, 0.009 * this.videoDuration);
      if (this.hasFinishedWatching) {
        clearInterval(interval);
      }
    },

    play() {
      this.isVideoPlayerSetUp && this.videoDom.play();
    },

    pause() {
      this.isVideoPlayerSetUp && this.videoDom.pause();
    },

    /**
     * Set up seek bar, or the timeline
     * Able to click anywhere on the bar or hold & drag to seek different time of video
     */
    setUpSeekBar() {
      const bodyDom = document.querySelector('body');
      const _updateCurrentTime = (event) => {
        const progressBarFullWidth = this.progressBarFullDom?.getBoundingClientRect().width ?? 0;
        const pageX = event.touches ? event.touches[0].pageX : event.pageX;
        const clickedPoint = pageX - this.progressBarFullDom?.getBoundingClientRect().left ?? 0;
        if (this.videoDom && progressBarFullWidth) {
          this.videoDom.currentTime = (clickedPoint / progressBarFullWidth) * this.videoDuration;
        }
        return clickedPoint;
      };

      this.progressBarFullDom.addEventListener(
        'click',
        (event) => {
          _updateCurrentTime(event);
          this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
        },
        this.listenerOptions,
      );

      const onMoveHandler = (event) => {
        event.stopPropagation();
        event.preventDefault();
        // Move progress if user is dragging the seeker
        this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
        _updateCurrentTime(event);
        this.updateProgress(this.videoDom?.currentTime ?? 0);
        // this.progressIndicatorDom.style.transform = `translateX(${clickedPoint - 6}px)`;
      };

      let timelineInteractionInterval = null;
      const onStartInteractionHandler = (event) => {
        event.stopPropagation();
        this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
        timelineInteractionInterval = setInterval(() => this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING), 500);
        if (bodyDom) {
          bodyDom.classList.add('unselectable');
        }

        document.addEventListener('mousemove', onMoveHandler);
        document.addEventListener('touchmove', onMoveHandler);
      };

      this.progressBarFullDom.addEventListener('mousedown', onStartInteractionHandler, this.listenerOptions);
      this.progressBarFullDom.addEventListener('touchstart', onStartInteractionHandler, this.listenerOptions);

      const onEndInteractionHandler = () => {
        clearInterval(timelineInteractionInterval);
        if (bodyDom) {
          bodyDom.classList.remove('unselectable');
        }

        document.removeEventListener('mousemove', onMoveHandler);
        document.removeEventListener('touchmove', onMoveHandler);
      };

      document.addEventListener('mouseup', onEndInteractionHandler, this.listenerOptions);
      document.addEventListener('touchend', onEndInteractionHandler, this.listenerOptions);

      // Prevent people from swiping when video is playing
      this.progressBarDom.addEventListener(
        'touchstart',
        (event) => {
          if (this.isPlaying) {
            event.stopPropagation();
          }
        },
        this.listenerOptions,
      );
    },

    /**
     * Adjust the length, or the width, of the progress bar according to the time
     * @param {number} currentTime
     */
    updateProgress(currentTime) {
      const progressBarFullWidth = this.progressBarFullDom.getBoundingClientRect().width;
      const loadedPercentageProgress = (currentTime / this.videoDuration) * progressBarFullWidth;
      // this.progressIndicatorDom.style.transform = `translateX(${loadedPercentageProgress}px)`;
      this.progressBarDom.style.width = `${loadedPercentageProgress}px`;
    },

    /**
     * Set up fullscreen action and behavior
     * Also if API allows, rotates the screen to landscape mode when playing will enter fullscreen
     * This also prevents the video from entering Picture-in-Picture mode on Android Oreo
     */
    setUpFullscreen() {
      const rfArr = ['requestFullscreen', 'webkitRequestFullscreen', 'mozRequestFullScreen', 'msRequestFullscreen'];
      const requestFullscreen = rfArr.filter((value) => value in document.documentElement);
      if (!requestFullscreen.length) {
        this.fullScreenDom.style.display = 'none';
        return;
      }

      const triggerFullScreen = () => {
        if (DeviceUtils.isiOS()) {
          this.videoDom.webkitEnterFullscreen();
        } else {
          this.videoDom[requestFullscreen[0]]();
        }
      };
      if (requestFullscreen.length || this.videoDom.webkitEnterFullscreen) {
        this.fullScreenDom.style.display = 'block';
        this.fullScreenDom.addEventListener('click', triggerFullScreen, this.listenerOptions);
      } else {
        this.fullScreenDom.style.display = 'none';
      }

      const rfEventArr = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
      const fullscreenchange = rfEventArr.filter((value) => `on${value}` in document);
      if (fullscreenchange.length) {
        document.addEventListener(fullscreenchange[0], () => {
          if (this.isVideoFullScreen) {
            this.videoDom.style.objectFit = 'contain';
            this.showNativeVideoControl(true);
          } else {
            this.videoDom.style.objectFit = 'fill';
            this.showNativeVideoControl(false);
          }
        });
      }

      const triggerExitFullScreen = () => {
        const exitFullScreen = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
        exitFullScreen.bind(document)();
      };

      // eslint-disable-next-line no-restricted-globals
      const orientation = screen.orientation || screen.msOrientation || screen.mozOrientation;
      if (orientation && orientation.addEventListener) {
        orientation.addEventListener('change', () => {
          if (orientation.type.indexOf('landscape') !== -1) {
            if (this.isPlaying && !this.isVideoFullScreen) {
              triggerFullScreen();
            }
          } else if (this.isPlaying && this.isVideoFullScreen) {
            triggerExitFullScreen();
          }
        });
      }

      // Hack for preventing picture in picture mode on Android 8.0
      window.addEventListener('resize', () => {
        if (!this.isVideoFullScreen) {
          return;
        }

        if ('screen' in window) {
          const minimumScreenSize = 0.33;
          const screenArea = window.screen.width * window.screen.height;
          const windowArea = window.outerHeight * window.outerWidth;

          // Assuming we are in Picture-in-Picture mode
          if (windowArea / screenArea < minimumScreenSize) {
            triggerExitFullScreen();
          }
        }
      });
    },

    /**
     * Set up volume control
     * Can hold & drag to adjust volume and mute & unmute
     * Allows only mute & unmute on mobile and tablet
     */
    setUpVolume() {
      let isVolumeBarHeld = false;
      this.setVolume(DeviceUtils.isMobile() ? 0 : 0.5);

      this.disableHoverOnMobile(this.volumeControlDom);

      const onStatusChangedHandler = () => {
        this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
        if (this.videoDom.muted) {
          this.setVolume(this.savedVolume);
        } else {
          this.savedVolume = this.videoDom.volume;
          this.setVolume(0);
        }
      };

      // Toggle between mute and unmute
      const volumeStatusDom = this.volumeControlDom.querySelector('.volume-status');
      if (volumeStatusDom) {
        volumeStatusDom.addEventListener(DeviceUtils.isMobile() ? 'touchend' : 'click', onStatusChangedHandler, this.listenerOptions);
      }

      // Adjust the volume
      const volumeSliderDom = this.volumeControlDom.querySelector('.volume-slider');
      if (volumeSliderDom) {
        const volumeIndicator = volumeSliderDom.querySelector('.volume-slider-indicator');
        const volumeOverlay = volumeSliderDom.querySelector('.volume-slider-overlay');
        const volumeBase = volumeSliderDom.querySelector('.volume-slider-base');
        const _updateVolume = (event) => {
          const volumeBarFullHeight = volumeBase.getBoundingClientRect().height;
          let clickedPoint = volumeSliderDom.getBoundingClientRect().bottom - event.clientY - 16;
          let videoVolume = clickedPoint / volumeBarFullHeight;
          if (videoVolume > 1) {
            videoVolume = 1;
          }
          if (videoVolume < 0) {
            videoVolume = 0;
          }
          if (Number.isNaN(videoVolume)) {
            return;
          }
          this.setVolume(videoVolume);
          if (clickedPoint >= this.MAX_VOLUME_HEIGHT) {
            clickedPoint = this.MAX_VOLUME_HEIGHT;
          }
          if (clickedPoint <= 0) {
            clickedPoint = 0;
          }

          volumeIndicator.style.bottom = `${clickedPoint + 12}px`;
          volumeOverlay.style.height = `${clickedPoint}px`;
          this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
        };

        volumeSliderDom.addEventListener('click', _updateVolume, this.listenerOptions);

        let volumeInteractInterval = null;
        volumeSliderDom.addEventListener('mousedown', (event) => {
          event.stopPropagation();
          event.preventDefault();
          isVolumeBarHeld = true;
          this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
          volumeInteractInterval = setInterval(() => this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING), 500);
        });

        document.addEventListener(
          'mouseup',
          () => {
            isVolumeBarHeld = false;
            clearInterval(volumeInteractInterval);
          },
          this.listenerOptions,
        );

        document.addEventListener(
          'mousemove',
          (event) => {
            // Move progress if user is dragging the volume
            if (isVolumeBarHeld) {
              _updateVolume(event);
            }
          },
          this.listenerOptions,
        );
      }
    },

    /**
     * Show or hide the seek bar on the bottom
     * @param {boolean} toShow
     */
    showBottomBar(toShow) {
      this.progressBarFullDom.style.visibility = toShow ? 'visible' : 'hidden';
    },

    /**
     * Hide the player controls in 1500ms
     */
    setHideControlsTimeOut() {
      this.showControlsTimeOut = setTimeout(() => this.showControls(false), 1500);
    },

    setVolume(volume) {
      volume = parseFloat(volume);
      this.videoDom.volume = volume;
      this.videoVolume = volume;
      this.videoDom.muted = volume === 0;
    },

    toggleControls() {
      this.showControls(!this.isControlVisible);
    },

    disableHoverOnMobile(element) {
      // Disable hover effect when on mobile
      if (!element) {
        return;
      }

      if (DeviceUtils.isMobile()) {
        element.classList.add('mobile-disable-hover');
      } else {
        element.classList.remove('mobile-disable-hover');
      }
    },

    showControls(toShow) {
      this.isControlVisible = toShow;
      this.toShowControls = toShow;
      this.emitEvent(toShow ? EVENT_ARTICLE_VIDEO_CONTROL_VISIBLE : EVENT_ARTICLE_VIDEO_CONTROL_HIDDEN);
    },

    onVideoInteract() {
      clearTimeout(this.showControlsTimeOut);
      this.showControls(true);
      if (this.isPlaying) {
        this.setHideControlsTimeOut();
      }
    },

    includeElement(el) {
      el.addEventListener('mouseover', () => {
        this.emitEvent(EVENT_ARTICLE_VIDEO_INTERACTING);
      });
    },

    /**
     * Check if the client can play HTML5 video or has streaming capability
     * Streaming mostly works on recent browsers and IE 11 with Windows 8.1
     * IE 10 has a lot of problems.
     * @returns {boolean}
     */
    checkCompatibility() {
      if (this.isCompatible !== null && this.isCompatible !== undefined) {
        return this.isCompatible;
      }

      // Return false if it's IE 10 or it cannot play any video
      if (/MSIE 10.0/i.test(navigator.userAgent) || typeof this.videoDom.canPlayType !== 'function') {
        return false;
      }

      const shakaSupported = ShakaPlayer.Player.isBrowserSupported();

      const canPlay = this.videoDom.canPlayType('video/mp4');
      // Check if Shaka player can be used or if it can play MP4
      if (shakaSupported || canPlay === 'maybe' || canPlay === 'probably') {
        return true;
      }
      return false;
    },

    checkSupported() {
      this.isCompatible = this.checkCompatibility();
      if (!this.isCompatible) {
        this.unsupportedDom.classList.remove('hidden');
        this.playBtnDom.classList.add('hidden');
        this.progressBarDom.classList.add('hidden');
        this.fullScreenDom.classList.add('hidden');
        this.volumeControlDom.classList.add('hidden');
        this.trackEvent(ANALYTICS_EVENT.unsupported);
      }
    },

    emitEvent(eventType) {
      this.$emit(eventType);
    },

    getBandwidthEstimate() {
      const connection = navigator.connection || navigator.moxConnection || navigator.webkitConnection;
      if (connection && 'downlink' in connection) {
        return connection.downlink * 1e6;
      }
      return 3e6;
    },

    getResWithWidth(width) {
      if (width === null || width === undefined) {
        return null;
      }
      switch (width) {
        case 1920:
          return 1080;
        case 1280:
          return 720;
        case 960:
          return 540;
        case 640:
          return 360;
        case 480:
          return 270;
        default:
      }
    },

    sendProgress() {
      const progress = this.percentageArray.length;

      if (this.progressThresholds.length && progress >= this.progressThresholds[0]) {
        this.currentProgress = this.progressThresholds.shift();
        this.trackEvent(ANALYTICS_EVENT.progress, {
          progress: this.currentProgress,
        });
      }
    },

    /**
     * Create the object that will be sent to analytics
     * @param {string} name Event name
     * @param {Object} data Extra data that needs to be passed
     */
    trackEvent(name, data) {
      const obj = {
        ...data,
      };

      if (this.pageType === 'product') {
        obj.page = 'product';
        obj.sku = (this.product || {}).skuNo;
      }

      if (this.videoDom?.videoWidth) {
        obj.resolution = this.getResWithWidth(this.videoDom?.videoWidth);
      }

      if (this.isVideoPlayerSetUp && this.shakaPlayer) {
        obj.bandwidth = Math.round(this.shakaPlayer.getStats().estimatedBandwidth);
      }

      this.sendEvent(name, obj);
    },

    /**
     * Send the event object to analytics, right now it's only HeapAnalytics
     * @param {string} name Event name
     * @param {object} obj  Object sent to HeapAnalytics
     */
    sendEvent(name, obj) {
      if ('heap' in window) {
        window.heap.track(name, obj);
      }
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

$transition-time: 750ms;

@define-mixin absoluteCenter {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
}

.article-video-image {
  position: absolute;
  z-index: -1;
}

.article-video {
  &,
  *,
  *:active,
  *:hover {
    -webkit-tap-highlight-color: transparent;
  }

  video {
    background: white;
    width: 100%;
    height: 100%;
    display: block;

    .disable-controls {
      &::-webkit-media-controls {
        display: none !important;
      }
    }
  }

  .article-video-loading {
    @mixin loader 48px;
    @mixin absoluteCenter;
  }

  .article-video-play-button {
    @mixin absoluteCenter;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    width: 88px;
    height: 88px;
    padding: 24px;
    visibility: visible;
    transition: opacity $transition-time ease, transform 500ms ease;
    cursor: pointer;

    .icon-play,
    .icon-pause {
      transform-origin: center;
      transition: transform 150ms ease-in-out;
      width: 50%;
      fill: var(--coral);
    }

    /* purgecss ignore */
    &:not(.mobile-disable-hover):hover {
      .icon-play,
      .icon-pause {
        transform: scale(1.2);
      }
    }

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      width: 72px;
      height: 72px;
      .play-caption {
        font-size: var(--font-size-16);
      }
    }

    img {
      width: 88px;
      height: 88px;
      position: absolute;
      display: none;
      color: var(--accent-color);
      transition: all $transition-time ease;
    }

    .play-caption {
      display: block;
      position: absolute;
      bottom: 0px;
      color: var(--white);
      font-family: var(--font-bold);
      font-size: var(--font-size-14);
    }
  }

  .article-video-bottom-bar {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 8px;
    background: var(--grey-light);
    cursor: default;

    transition: none !important;

    .loaded-progress-bar {
      background: var(--accent-color);
      width: 0px;
      height: 8px;
      transform: translate3d(0, 0, 0);
    }

    .loaded-progress-indicator {
      background: var(--accent-color);
      position: absolute;
      left: 0px;
      bottom: -2px;
    }
  }

  .article-video-full-screen {
    width: 36px;
    height: 36px;
    display: none;
    position: absolute;
    right: 40px;
    @media (max-width: $mobile-breakpoint) {
      right: 24px;
    }
    top: 24px;
    padding: 1px;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.7);
    transition: opacity $transition-time ease;
    border-radius: 25px;

    svg {
      box-sizing: border-box;
      padding: 8px;
      width: 36px;
      opacity: 0.5;
    }
  }

  .article-video-volume {
    position: absolute;
    display: flex;
    flex-direction: column;
    top: 24px;
    right: 92px;
    @media (max-width: $mobile-breakpoint) {
      right: 74px;
    }
    width: 36px;
    height: 36px;
    padding: 1px;
    transition: opacity $transition-time ease, height 400ms ease;
    overflow: hidden;
    cursor: pointer;
    border-radius: 25px;

    img {
      display: none;
      box-sizing: border-box;
      padding: 8px;
    }

    /* purgecss ignore */
    &:hover {
      height: 190px;

      .volume-slider {
        opacity: 1;
        visibility: visible;
        transition: opacity 200ms ease-in, height 400ms ease;
      }
    }

    .volume-status {
      width: 36px;
      height: 36px;
      z-index: 1;
      border-radius: 25px;
      background: rgba(255, 255, 255, 0.7);

      .icon-volume {
        width: 36px;
        padding: 8px;
        box-sizing: border-box;
        opacity: 0.5;
      }
    }

    .volume-slider {
      width: 36px;
      height: 190px;
      position: absolute;
      display: flex;
      justify-content: center;
      right: 0px;
      border-radius: 25px;
      overflow: hidden;
      opacity: 0;
      visibility: hidden;
      transition: opacity 200ms ease-in, visibility 0ms ease-in 200ms;
      background: rgba(255, 255, 255, 0.4);

      * {
        width: 8px;
        position: absolute;
        bottom: 16px;
      }

      .volume-slider-indicator {
        width: 12px;
        height: 12px;
        background: var(--coral);
        z-index: 2;
        bottom: 72px;
      }

      .volume-slider-overlay {
        height: 64px;
        background: var(--coral);
        z-index: 1;
        max-height: 120px;
        border-radius: 25px;
      }

      .volume-slider-base {
        height: 120px;
        background: white;
        border-radius: 25px;
      }

      @media (max-width: calc($mobile-breakpoint - 1px)) {
        visibility: hidden !important;
      }
    }
  }

  .controls {
    &.hide-controls,
    &.hide-controls img {
      opacity: 0;
      visibility: hidden;
      transition: opacity $transition-time ease, visibility 0s ease $transition-time;
    }
  }

  .article-video-not-supported {
    background: rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100% - 8px);
    display: flex;
    justify-content: center;
    align-items: center;

    h4 {
      color: var(--white);
    }
  }
}
</style>
